import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { z } from "zod";

// Enums extracted from Ruby model for permissions
export const statusOptions = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "blocked", label: "Blocked" },
];

export const levelOptions = [
  { value: "0", label: "Read Only" },
  { value: "1", label: "Read Write" },
  { value: "2", label: "Admin" },
];

export const categoryOptions = [
  { value: "0", label: "User" },
  { value: "1", label: "Admin" },
  { value: "2", label: "System" },
];

export const filterPermissionsSchema = z.object({
  user_id_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  admin_user_id_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  strategy_id_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  type_contains: z.string().nullable().optional(),
  status_contains: z.string().nullable().optional(),
  level_contains: z.string().nullable().optional(),
  category_contains: z.string().nullable().optional(),
  max_issuer_count_greater_than: z
    .preprocess((val) => {
      const n = Number(val);
      return isNaN(n) ? undefined : n;
    }, z.number())
    .optional(),
  // Date fields at the end
  created_at_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  created_at_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  updated_at_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  updated_at_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
});

export type FilterPermissionsSchema = z.infer<typeof filterPermissionsSchema>;

export const fields: Field[] = [
  {
    name: "user_id_contains",
    label: "User",
    type: "asyncAutocomplete",
    base_url: "legacy-quantilia/users",
    column_name: "name",
  },
  {
    name: "admin_user_id_contains",
    label: "Admin User",
    type: "asyncAutocomplete",
    base_url: "legacy-quantilia/users",
    column_name: "name",
  },
  {
    name: "strategy_id_contains",
    label: "Strategy",
    type: "asyncAutocomplete",
    base_url: "legacy-quantilia/strategies",
    column_name: "name",
  },
  {
    name: "type_contains",
    label: "Type",
    type: "input",
    input_type: "text",
  },
  {
    name: "status_contains",
    label: "Status",
    type: "select",
    useTranslation: false,
    options: statusOptions,
  },
  {
    name: "level_contains",
    label: "Level",
    type: "select",
    useTranslation: false,
    options: levelOptions,
  },
  {
    name: "category_contains",
    label: "Category",
    type: "select",
    useTranslation: false,
    options: categoryOptions,
  },
  {
    name: "max_issuer_count_greater_than",
    label: "Max Issuer Count Greater Than",
    type: "input",
    input_type: "number",
    allowZero: true,
    allowNegative: false,
  },
  // Date filters at end
  {
    name: "created_at_from",
    label: "Created At From",
    type: "date",
  },
  {
    name: "created_at_to",
    label: "Created At To",
    type: "date",
  },
  {
    name: "updated_at_from",
    label: "Updated At From",
    type: "date",
  },
  {
    name: "updated_at_to",
    label: "Updated At To",
    type: "date",
  },
];

export const client = "legacy-quantilia";

export function FilterPermissions() {
  return (
    <FilterTable
      client={client}
      schema={filterPermissionsSchema}
      tableName="permissions"
      customBuildPath={() => `${client}/level_permissions`} // this line is good don't change it
      fields={fields}
      columns={2}
    />
  );
}
